import { useFormik } from 'formik'
import { FC, useEffect, useState } from 'react'
import * as Yup from 'yup'
import { FormInput } from '../Form/FormInput'
import { useLocation } from 'react-router-dom'
import { useModal } from './ModalContext'
import {
  ButtonType,
  DocumentoPessoaLabels,
  DocumentoPessoaType,
  MaskType,
  QueryState,
  showError,
  stringifyRequestQuery,
} from '../../../_metronic/helpers'
import { getPessoas, getUserByDocumento, getUserByPessoa } from '../../modules/auth/core/_requests'
import { PacienteProntuarioModal } from '../../modules/apps/paciente-management/pacientes-list/components/modal/PacienteProntuarioModal'
import { PacienteModal } from '../../modules/apps/paciente-management/pacientes-list/components/modal/PacienteModal'
import { FuncionarioVincularModal } from '../../modules/apps/funcionarios/funcionarios-list/components/modal/FuncionarioVincularModal'
import { FuncionarioModal } from '../../modules/apps/funcionarios/funcionarios-list/components/modal/FuncionarioModal'
import { FormRadio } from '../Form/FormRadio'
import { InterconsultaModal } from '../../modules/apps/pareceres/pareceres-list/components/modal/InterconsultaModal'
import { LoadingIndicator } from '../Loading/LoadingIndicator'
import { PessoaUsuarioList } from '../../modules/auth'
import { FormDateInput } from '../Form/FormDateInput'
import { Button } from '..'
import { useT } from '_metronic/i18n/Metronici18n'
import { UsuarioDtoVinculo } from 'app/modules/apps/admin/usuarios/usuarios-list/core/_models'

const validationSchema = Yup.object().shape({
  documento: Yup.string().required('O campo documento é obrigatório'),
  cpf: Yup.string()
    .trim()
    .when('documento', {
      is: (documento: DocumentoPessoaType) => documento === DocumentoPessoaType.Cpf,
      then: (schema) =>
        schema
          .matches(/^\d{3}\.\d{3}\.\d{3}-\d{2}$/, 'CPF inválido')
          .required('O campo CPF é obrigatório'),
      otherwise: (schema) => schema.notRequired(),
    }),
  cns: Yup.string()
    .trim()
    .when('documento', {
      is: (documento: DocumentoPessoaType) => documento === DocumentoPessoaType.Cns,
      then: (schema) => schema.required('O campo CNS é obrigatório'),
      otherwise: (schema) => schema.notRequired(),
    }),
  passaporte: Yup.string()
    .trim()
    .when('documento', {
      is: (documento: DocumentoPessoaType) => documento === DocumentoPessoaType.Passaporte,
      then: (schema) => schema.required('O campo passaporte é obrigatório'),
      otherwise: (schema) => schema.notRequired(),
    }),
  bilheteIdentidadeAngola: Yup.string()
    .trim()
    .when('documento', {
      is: (documento: DocumentoPessoaType) =>
        documento === DocumentoPessoaType.BilheteIdentidadeAngola,
      then: (schema) => schema.required('O campo Bilhete de Identidade Nacional é obrigatório'),
      otherwise: (schema) => schema.notRequired(),
    }),
  primeiroNome: Yup.string().notRequired(),
  sobreNome: Yup.string().notRequired(),
  dataNascimento: Yup.string().notRequired(),
})

type IProps = {
  forcedEntityType?: 'paciente' | 'funcionario' | 'profissional-saude' | 'interconsulta'
}

export type IDocumento = {
  numero: string
  tipo: DocumentoPessoaType
}

export type IInfo = {
  primeiroNome?: string
  sobreNome?: string
  dataNascimento?: string
  documento: IDocumento
}

export const SearchPersonModal: FC<IProps> = ({ forcedEntityType }) => {
  const { closeModal, showModal } = useModal()
  const { pathname } = useLocation()
  const { t } = useT()

  const [entityType, setEntityType] = useState<
    'paciente' | 'funcionario' | 'profissional-saude' | 'interconsulta' | null
  >(null)
  const [showExtraSearch, setShowExtraSearch] = useState(false)
  const [possiblePeople, setPossiblePeople] = useState<PessoaUsuarioList[]>([])
  const [documentoValorAtual, setDocumentoValorAtual] = useState<string>('')

  const formik = useFormik({
    initialValues: {
      cpf: '',
      cns: '',
      bilheteIdentidadeAngola: '',
      passaporte: '',
      documento: DocumentoPessoaType.Cpf,
      primeiroNome: '',
      sobreNome: '',
      dataNascimento: '',
    },
    validationSchema,

    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      const documentoValor =
        values.documento === DocumentoPessoaType.Cpf
          ? values.cpf
          : values.documento === DocumentoPessoaType.Cns
          ? values.cns
          : values.documento === DocumentoPessoaType.Passaporte
          ? values.passaporte
          : values.bilheteIdentidadeAngola

      if (!documentoValor) {
        setSubmitting(false)
        return
      }
      try {
        const data = await getUserByDocumento(values.documento, documentoValor)
        abrirModalVinculo(data, documentoValor)
      } catch (error: any) {
        if (error?.response?.status === 404) {
          if (!showExtraSearch) {
            setDocumentoValorAtual(documentoValor)
            setShowExtraSearch(true)
            setSubmitting(false)
            return
          }

          if (values.primeiroNome && values.sobreNome && values.dataNascimento) {
            const state: QueryState = {
              page: 0,
              size: 5,
              search: `primeiroNome~*${values.primeiroNome},sobreNome~*${values.sobreNome},dataNascimento:${values.dataNascimento}`,
            }
            const response = await getPessoas(stringifyRequestQuery(state))
            const sugestoes = response?.data || []

            if (sugestoes.length > 0) {
              setPossiblePeople(sugestoes)
              setSubmitting(false)
              return
            }
          }

          abrirModalCriacao(documentoValor)
          return
        }
      } finally {
        setSubmitting(false)
      }
    },
  })

  const abrirModalVinculo = (data: UsuarioDtoVinculo, documentoValor: string) => {
    const documento: IDocumento = {
      tipo: formik.values.documento,
      numero: documentoValor,
    }
    const info: IInfo = {
      primeiroNome: formik.values.primeiroNome || undefined,
      sobreNome: formik.values.sobreNome || undefined,
      dataNascimento: formik.values.dataNascimento || undefined,
      documento,
    }
    if (entityType === 'paciente') {
      showModal({
        title: 'Vincular Paciente',
        content: <PacienteProntuarioModal documento={documento} nome={data.nome as string} />,
      })
    } else if (entityType === 'funcionario') {
      showModal({
        title: 'Vincular Funcionário',
        content: <FuncionarioVincularModal documento={documento} usuario={data} />,
      })
    } else if (entityType === 'interconsulta') {
      showModal({
        title: 'Solicitar Parecer/Interconsulta',
        content: <InterconsultaModal info={info} pessoa={data?.pessoa} />,
      })
    }
  }

  const abrirModalCriacao = (documentoValor: string) => {
    const info: IInfo = {
      primeiroNome: formik.values.primeiroNome || undefined,
      sobreNome: formik.values.sobreNome || undefined,
      dataNascimento: formik.values.dataNascimento || undefined,
      documento: { tipo: formik.values.documento, numero: documentoValor },
    }

    if (entityType === 'paciente') {
      showModal({
        title: 'Adicionar Paciente',
        content: <PacienteModal info={info} />,
      })
    } else if (entityType === 'funcionario') {
      showModal({
        title: 'Adicionar Funcionário',
        content: <FuncionarioModal info={info} />,
      })
    } else if (entityType === 'interconsulta') {
      showModal({
        title: 'Solicitar Parecer/Interconsulta',
        content: <InterconsultaModal info={info} />,
      })
    }
  }

  useEffect(() => {
    if (forcedEntityType) {
      setEntityType(forcedEntityType)
      return
    }

    if (pathname.includes('pacientes')) setEntityType('paciente')
    else if (pathname.includes('profissional-saude')) setEntityType('profissional-saude')
    else if (pathname.includes('funcionarios')) setEntityType('funcionario')
  }, [pathname, forcedEntityType])

  const handleVincularPessoa = async (pessoaId: number) => {
    try {
      const data = await getUserByPessoa(pessoaId)
      const documento = data.pessoa?.cpf || data.pessoa?.cns || documentoValorAtual
      abrirModalVinculo(data, documento)
    } catch (error) {
      showError(error)
    }
  }

  return (
    <>
      <form onSubmit={formik.handleSubmit} noValidate>
        {showExtraSearch ? (
          <>
            {possiblePeople.length > 0 ? (
              <>
                <div className='alert alert-info'>
                  Encontramos possíveis pessoas já cadastradas:
                </div>

                {possiblePeople.map((pessoa) => (
                  <div className='row mb-3' key={pessoa.id}>
                    <div className='col-md-8'>
                      <div className='fs-5 fw-bold'>{pessoa.nome}</div>
                      {pessoa.dataNascimento && (
                        <div className='text-muted fs-7'>
                          Data de nascimento: {pessoa.dataNascimentoFormatada}
                        </div>
                      )}
                      {(pessoa.cpf || pessoa.cns) && (
                        <div className='text-muted fs-7'>
                          <span>
                            Documento: <strong>{pessoa.cpf || pessoa.cns}</strong>
                          </span>
                        </div>
                      )}
                    </div>
                    <div className='d-flex flex-end col-md-4'>
                      <Button
                        size='btn-sm'
                        className='btn-primary mt-2'
                        onClick={() => handleVincularPessoa(pessoa.id)}
                      >
                        Vincular a este cadastro
                      </Button>
                    </div>
                  </div>
                ))}
              </>
            ) : (
              <>
                <div className='alert alert-info mb-5'>
                  Não encontramos ninguém com este documento. Informe o nome e a data de nascimento
                  para verificar se já existe cadastro.
                </div>
                <div className='row'>
                  <div className='col-md-4'>
                    <FormInput
                      formik={formik}
                      placeholder='Digite o primeiro nome'
                      label='Primeiro Nome'
                      name='primeiroNome'
                    />
                  </div>
                  <div className='col-md-4'>
                    <FormInput
                      formik={formik}
                      placeholder='Digite o sobrenome'
                      label='Sobrenome'
                      name='sobreNome'
                    />
                  </div>
                  <div className='col-md-4'>
                    <FormDateInput
                      formik={formik}
                      label='Data de nascimento'
                      name='dataNascimento'
                      placeholder='Selecione uma data'
                    />
                  </div>
                </div>
              </>
            )}
          </>
        ) : (
          <>
            <FormRadio
              formik={formik}
              name='documento'
              label='Documento'
              options={[
                {
                  id: 1,
                  descricao: 'CPF',
                  value: DocumentoPessoaType.Cpf,
                },
                {
                  id: 2,
                  descricao: 'CNS',
                  value: DocumentoPessoaType.Cns,
                },
                {
                  id: 3,
                  descricao: 'Passaporte',
                  value: DocumentoPessoaType.Passaporte,
                },
                {
                  id: 4,
                  descricao: DocumentoPessoaLabels[DocumentoPessoaType.BilheteIdentidadeAngola],
                  value: DocumentoPessoaType.BilheteIdentidadeAngola,
                },
              ]}
              className='mb-5'
              isRequired
            />
            {formik.values.documento === DocumentoPessoaType.Cpf ? (
              <FormInput
                formik={formik}
                key={formik.values.documento}
                label={DocumentoPessoaLabels[formik.values.documento]}
                name='cpf'
                placeholder='Digite seu CPF'
                mask={MaskType.CPF}
                isRequired
              />
            ) : formik.values.documento === DocumentoPessoaType.Cns ? (
              <FormInput
                formik={formik}
                key={formik.values.documento}
                label={DocumentoPessoaLabels[formik.values.documento]}
                name='cns'
                placeholder='Digite seu CNS'
                mask={MaskType.CNS}
                isRequired
              />
            ) : formik.values.documento === DocumentoPessoaType.Passaporte ? (
              <FormInput
                formik={formik}
                key={formik.values.documento}
                label={DocumentoPessoaLabels[formik.values.documento]}
                name='passaporte'
                placeholder='Digite seu passaporte'
                isRequired
              />
            ) : formik.values.documento === DocumentoPessoaType.BilheteIdentidadeAngola ? (
              <FormInput
                formik={formik}
                key={formik.values.documento}
                label={DocumentoPessoaLabels[formik.values.documento]}
                name='bilheteIdentidadeAngola'
                placeholder='Digite seu bilhete de identidade'
                isRequired
              />
            ) : null}
          </>
        )}
        <div className='d-flex justify-content-center mt-10'>
          <Button
            type={ButtonType.Reset}
            className='btn-secondary me-3'
            onClick={closeModal}
            disabled={formik.isSubmitting}
          >
            {t('common.cancel')}
          </Button>
          {possiblePeople?.length > 0 ? (
            <Button className='btn-primary' onClick={() => abrirModalCriacao(documentoValorAtual)}>
              Criar novo cadastro
            </Button>
          ) : (
            <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
              {formik.isSubmitting ? <LoadingIndicator /> : t('common.verify')}
            </Button>
          )}
        </div>
      </form>
    </>
  )
}
